import React, { useEffect, useMemo, useState } from 'react';
import { Users, Gift, Copy, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import SectionLabel from '../ui-shree/SectionLabel';
import { fetchGuardiansCount, fetchGuardianProgress } from '../../lib/api';

/**
 * GuardianMilestone — live "X / 1,000" progress on /loyalty plus a quiet
 * referral panel ("Gift a ritual to 3 friends, earn 100 SEVA").
 *
 * The referrer is identified by their own Guardian email. The page
 * generates a personal share-link of the form
 *   /?ref=<email-encoded>
 * which the JoinTheCircleModal reads on Home — when a friend signs up
 * through it, the referrer's progress increments and at 3 friends the
 * backend mints the 100-SEVA bonus exactly once.
 */

const SHARE_TEMPLATE = (link) =>
  `I just inscribed myself as a Genesis Guardian at Shreesavaa — ` +
  `the cold-pressed, on-chain skincare house. Join through this link ` +
  `and you'll receive 10 SEVA as your Dharma Discount: ${link}`;

const GuardianMilestone = ({ testId = 'loyalty-guardian-milestone' }) => {
  const [count, setCount] = useState(null);     // { total_registered, founding_cap, ... }
  const [email, setEmail] = useState('');
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');

  useEffect(() => {
    fetchGuardiansCount().then(setCount).catch(() => {});
  }, []);

  const onLookup = async (e) => {
    e.preventDefault();
    setErr(''); setProgress(null);
    if (!email.trim()) return;
    setLoading(true);
    try {
      const p = await fetchGuardianProgress(email.trim().toLowerCase());
      setProgress(p);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(
        Array.isArray(detail)
          ? (detail[0]?.msg || 'Could not find that Guardian.')
          : (detail || 'Could not find that Guardian — please join the Circle first.'),
      );
    } finally {
      setLoading(false);
    }
  };

  const refLink = useMemo(() => {
    if (!progress?.email) return '';
    const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
    return `${origin}/?ref=${encodeURIComponent(progress.email)}`;
  }, [progress]);

  const onCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1800);
    } catch { /* ignore */ }
  };

  const totalRegistered = count?.total_registered ?? 0;
  const cap = count?.founding_cap ?? 1000;
  const pct = Math.min(100, Math.round((totalRegistered / cap) * 100 * 100) / 100);
  const inscribed = progress?.referrals_inscribed ?? 0;
  const threshold = progress?.referral_threshold ?? 3;
  const bonusAwarded = !!progress?.referral_bonus_awarded;
  const stepsPct = Math.min(100, (inscribed / threshold) * 100);

  return (
    <section
      id="guardian-milestone"
      data-testid={testId}
      className="px-6 md:px-12 py-24 md:py-32 bg-[#F4F8F9] border-t border-[#C1D7E2]"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Live milestone */}
          <div className="md:col-span-7">
            <SectionLabel testId="guardian-milestone-label">
              Guardian Milestone · live count
            </SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#7C9CB1] font-light leading-[1.05] mb-8">
              The Circle is being <em className="text-[#90B0BF]">written</em>.
            </h2>
            <p
              data-testid="guardian-milestone-headline"
              className="font-serif text-3xl md:text-5xl text-[#7C9CB1] font-light leading-none mb-5"
            >
              Current Guardians:{' '}
              <span data-testid="guardian-milestone-count" className="font-mono text-[#90B0BF]">
                {totalRegistered.toLocaleString()}
              </span>{' '}
              <span className="text-[#90B0BF]/80">/ {cap.toLocaleString()}</span>
            </p>

            {/* Slim progress bar */}
            <div
              data-testid="guardian-milestone-bar"
              className="h-px bg-[#C1D7E2] max-w-xl"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-px bg-[#7C9CB1] transition-all duration-700"
                style={{ width: `${Math.max(0.5, pct)}%` }}
              />
            </div>
            <p className="mt-3 font-mono text-[10px] tracking-[0.28em] uppercase text-[#90B0BF]">
              {pct.toFixed(2)}% of the founding circle inscribed
            </p>

            <p className="mt-8 text-[#7C9CB1]/85 font-light leading-relaxed max-w-xl">
              The first 1,000 Guardians are not buyers. They are witnesses —
              their names sit in the genesis of the ledger. Each one carries
              10 SEVA as their Dharma Discount, and a number that cannot be
              re-issued.
            </p>
          </div>

          {/* Founding stats chip */}
          <aside className="md:col-span-5">
            <div className="relative border border-[#C1D7E2] bg-[#F4F8F9] p-8 md:p-10">
              <span
                aria-hidden
                className="absolute top-0 left-8 right-8 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
                }}
              />
              <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] mb-5">
                <Users size={13} strokeWidth={1.5} /> Founding ledger
              </p>
              <ul className="space-y-3 font-mono text-[12px] text-[#7C9CB1]">
                <li className="flex items-baseline justify-between border-b border-[#C1D7E2]/60 pb-2">
                  <span className="tracking-[0.22em] uppercase text-[#90B0BF]">Inscribed</span>
                  <span className="text-[14px]">{totalRegistered}</span>
                </li>
                <li className="flex items-baseline justify-between border-b border-[#C1D7E2]/60 pb-2">
                  <span className="tracking-[0.22em] uppercase text-[#90B0BF]">Seats remaining</span>
                  <span className="text-[14px]">{Math.max(0, cap - totalRegistered)}</span>
                </li>
                <li className="flex items-baseline justify-between border-b border-[#C1D7E2]/60 pb-2">
                  <span className="tracking-[0.22em] uppercase text-[#90B0BF]">Dharma Discount</span>
                  <span>10 SEVA each</span>
                </li>
                <li className="flex items-baseline justify-between">
                  <span className="tracking-[0.22em] uppercase text-[#90B0BF]">Referral bonus</span>
                  <span>100 SEVA · 3 friends</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>

        {/* Referral panel — quiet growth loop */}
        <div
          id="referral"
          data-testid="loyalty-referral"
          className="mt-20 pt-16 border-t border-[#C1D7E2] grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16"
        >
          <div className="md:col-span-7">
            <SectionLabel testId="referral-label">
              Gift a ritual · the growth that earns itself
            </SectionLabel>
            <h3 className="font-serif text-3xl md:text-5xl text-[#7C9CB1] font-light leading-[1.05] mb-6">
              Invite <em className="text-[#90B0BF]">three</em>. Earn{' '}
              <span className="font-mono text-[#90B0BF]">100 SEVA</span>.
            </h3>
            <p className="text-[#7C9CB1]/85 font-light leading-relaxed max-w-xl">
              Share your personal link with three friends. When their names
              are inscribed in the Circle, the ledger signs a further{' '}
              <span className="font-mono text-[13px]">100 SEVA</span> against
              yours — once, witnessed by Indra. No streaks, no expiries.
            </p>

            <form
              onSubmit={onLookup}
              data-testid="referral-form"
              className="mt-8 max-w-md flex items-end gap-3 border-b border-[#7C9CB1]/30 pb-3"
            >
              <label className="flex-1 block">
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
                  Your Guardian email
                </span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@somewhere.com"
                  data-testid="referral-email-input"
                  className="mt-1 w-full bg-transparent text-[#7C9CB1] placeholder-[#90B0BF]/60 font-serif text-lg focus:outline-none"
                />
              </label>
              <button
                type="submit"
                disabled={loading || !email.trim()}
                data-testid="referral-submit"
                className="px-5 py-2 text-[10px] tracking-[0.24em] uppercase font-medium text-[#F4F8F9] bg-[#7C9CB1] hover:bg-[#90B0BF] disabled:opacity-50 transition-colors"
              >
                {loading ? <Loader2 size={12} className="animate-spin" /> : 'Show my link'}
              </button>
            </form>
            {err && (
              <p data-testid="referral-error" className="mt-3 font-mono text-[11px] text-[#9c2a2a]">
                {err}
              </p>
            )}
          </div>

          {/* Personal share card */}
          <aside className="md:col-span-5">
            {!progress && (
              <div
                data-testid="referral-empty-card"
                className="border border-dashed border-[#C1D7E2] bg-[#F4F8F9]/60 p-8 md:p-10 text-center"
              >
                <Gift size={22} strokeWidth={1.2} className="text-[#90B0BF] mx-auto mb-4" />
                <p className="font-serif italic text-lg text-[#7C9CB1]/80 leading-relaxed">
                  Enter your Guardian email to receive your personal share
                  link and current referral progress.
                </p>
              </div>
            )}

            {progress && (
              <div
                data-testid="referral-result-card"
                className="border border-[#7C9CB1] bg-[#7C9CB1] text-[#F4F8F9] p-8 md:p-10"
              >
                <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/75 mb-5">
                  <Sparkles size={13} strokeWidth={1.5} />
                  Guardian #{progress.guardian_number}
                </p>
                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4F8F9]/70 mb-1">
                  Referral progress
                </p>
                <p
                  data-testid="referral-progress-headline"
                  className="font-serif text-3xl md:text-4xl font-light leading-none"
                >
                  {inscribed} <span className="text-[#F4F8F9]/75">/ {threshold}</span>{' '}
                  <span className="font-mono text-[12px] tracking-[0.2em] text-[#F4F8F9]/70 align-baseline">
                    friends inscribed
                  </span>
                </p>
                <div
                  className="mt-3 h-px bg-[#F4F8F9]/30"
                  role="progressbar"
                  aria-valuenow={stepsPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="h-px bg-[#F4F8F9] transition-all duration-700"
                    style={{ width: `${Math.max(2, stepsPct)}%` }}
                  />
                </div>
                <p
                  data-testid="referral-bonus-status"
                  className="mt-4 font-mono text-[10px] tracking-[0.26em] uppercase text-[#F4F8F9]/85"
                >
                  {bonusAwarded
                    ? '✓ 100 SEVA bonus already signed'
                    : `${Math.max(0, threshold - inscribed)} more · 100 SEVA pending`}
                </p>

                {/* Share link */}
                <div className="mt-7 pt-5 border-t border-[#F4F8F9]/25">
                  <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#F4F8F9]/70 mb-2">
                    Your share link
                  </p>
                  <p
                    data-testid="referral-link"
                    className="font-mono text-[11px] break-all text-[#F4F8F9] mb-4"
                  >
                    {refLink}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onCopy(refLink, 'link')}
                      data-testid="referral-copy-link"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#F4F8F9]/40 text-[10px] tracking-[0.22em] uppercase text-[#F4F8F9] hover:bg-[#F4F8F9]/10"
                    >
                      <Copy size={11} strokeWidth={1.6} />
                      {copied === 'link' ? 'Copied' : 'Copy link'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onCopy(SHARE_TEMPLATE(refLink), 'msg')}
                      data-testid="referral-copy-message"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#F4F8F9]/40 text-[10px] tracking-[0.22em] uppercase text-[#F4F8F9] hover:bg-[#F4F8F9]/10"
                    >
                      <Copy size={11} strokeWidth={1.6} />
                      {copied === 'msg' ? 'Copied' : 'Copy message'}
                    </button>
                  </div>
                </div>

                {/* Already-inscribed friends */}
                {progress.referred_friends?.length > 0 && (
                  <ul className="mt-7 pt-5 border-t border-[#F4F8F9]/25 space-y-2 font-mono text-[11px] text-[#F4F8F9]/90">
                    {progress.referred_friends.slice(0, 5).map((f) => (
                      <li
                        key={f.email}
                        className="flex items-baseline justify-between gap-4"
                      >
                        <span className="flex items-center gap-2">
                          <CheckCircle2 size={11} strokeWidth={1.6} />
                          Guardian #{f.guardian_number}
                        </span>
                        <span className="text-[#F4F8F9]/70 text-[10px]">{f.email}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </aside>
        </div>
      </div>
    </section>
  );
};

export default GuardianMilestone;
